import React, { useState, useMemo, useEffect } from 'react';
import { MainLayout } from '@/components/layouts/MainLayout';
import { PeriodFilter, PeriodFilterType, getDateRangeFromPeriod } from '@/components/common/PeriodFilter';
import { useSales } from '@/hooks/useSales';
import { useWithdrawals } from '@/hooks/useWithdrawals';
import { useSupabaseInventory } from '@/context/SupabaseInventoryContext';
import { useCompany } from '@/context/CompanyContext';
import { useSubscription } from '@/context/SubscriptionContext';
import { BarChart3, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { EditableGrid } from '@/components/dashboard/EditableGrid';
import { EditModeToggle } from '@/components/dashboard/EditModeToggle';
import { AddWidgetButton } from '@/components/dashboard/AddWidgetButton';
import { AnalyticsWidget } from '@/components/analytics/AnalyticsWidget';
import { useDashboardLayout } from '@/hooks/useDashboardLayout';
export const Analytics: React.FC = () => {
  const [periodFilter, setPeriodFilter] = useState<PeriodFilterType>('week');
  const [statusFilter, setStatusFilter] = useState<'all' | 'nouveau' | 'best_seller' | 'en_promotion' | 'fin_de_serie' | 'archive'>('all');
  const [rowsPerPage, setRowsPerPage] = useState<number>(20);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [viewMode, setViewMode] = useState<'ventes' | 'retraits'>('ventes');
  
  const {
    widgets,
    isEditMode,
    reorderWidgets,
    updateWidget,
    removeWidget,
    addWidget,
    addWidgetAt,
    toggleEditMode,
    cancelEdit,
  } = useDashboardLayout(viewMode === 'ventes' ? 'analytics' : 'retrait-analytics');

  // Calculer s'il y a de la place pour un nouveau widget
  const hasSpaceForNewWidget = useMemo(() => {
    if (!widgets.length) return true;
    
    // Calculer l'espace utilisé dans la grille (sur 4 colonnes max)
    let usedColumns = 0;
    widgets.forEach(widget => {
      switch (widget.size) {
        case 'small':
          usedColumns += 1; // 1/4 de la largeur
          break;
        case 'medium':
          usedColumns += 2; // 1/2 de la largeur
          break;
        case 'full':
          usedColumns += 4; // pleine largeur
          break;
        default:
          usedColumns += 2;
      }
    });
    
    // S'il reste au moins une colonne de libre
    return (usedColumns % 4) !== 0 || usedColumns < 12; // Limite à 3 rangées
  }, [widgets]);
  const {
    sales,
    loading: salesLoading,
    refetch: refetchSales
  } = useSales();
  const {
    withdrawals,
    loading: withdrawalsLoading,
    refetch: refetchWithdrawals
  } = useWithdrawals();
  const {
    products,
    loading: productsLoading,
    refreshProducts
  } = useSupabaseInventory();
  const {
    userRole
  } = useCompany();
  const {
    hasAccess
  } = useSubscription();
  useEffect(() => {
    document.title = 'Analyse des performances — Chart-Flow';
  }, []);
  const lastUpdate = new Date().toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
  const productById = useMemo(() => Object.fromEntries(products.map(p => [p.id, p])), [products]);
  const filteredSales = useMemo(() => {
    const {
      start,
      end
    } = getDateRangeFromPeriod(periodFilter);
    return sales.filter(sale => {
      if ((sale.total_amount ?? 0) <= 0) return false; // exclure retraits
      const d = new Date(sale.sale_date);
      if (d < start || d > end) return false;
      if (statusFilter === 'all') return true;
      const prod = productById[sale.product_id];
      return prod?.cycle_de_vie === statusFilter;
    });
  }, [sales, periodFilter, statusFilter, productById]);
  const kpiData = useMemo(() => {
    const totalRevenue = filteredSales.reduce((sum, sale) => sum + sale.total_amount, 0);
    const totalSales = filteredSales.length;
    const totalItemsSold = filteredSales.reduce((sum, sale) => sum + sale.quantity_sold, 0);
    const lowStockProducts = products.filter(product => product.quantity <= product.min_threshold && product.min_threshold > 0).length;
    const averageBasket = totalSales > 0 ? totalRevenue / totalSales : 0;
    return {
      totalRevenue,
      totalSales,
      totalItemsSold,
      lowStockProducts,
      averageBasket
    };
  }, [filteredSales, products]);
  const handleRefresh = async () => {
    await Promise.all([refetchSales(), refreshProducts()]);
  };
  const isLoading = salesLoading || productsLoading;
  const getPeriodLabel = () => {
    switch (periodFilter) {
      case 'today':
        return "aujourd'hui";
      case 'week':
        return 'sur 7 jours';
      case 'month':
        return 'sur 30 jours';
      default:
        return 'depuis toujours';
    }
  };
  const formatPrice = (price: number) => new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR'
  }).format(price);
  const totalPages = useMemo(() => Math.max(1, Math.ceil(filteredSales.length / rowsPerPage)), [filteredSales, rowsPerPage]);
  
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);
  
  useEffect(() => {
    setCurrentPage(1);
  }, [periodFilter, statusFilter, rowsPerPage]);

  return (
    <MainLayout title="Analyse des performances">
      <div className="space-y-6">
        {/* Bannière supérieure */}
        <section 
          aria-label="Informations et filtres de la page" 
          className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent rounded-lg p-4 lg:p-6 border"
        >
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-foreground mb-2">Analyse des ventes</h1>
              <p className="text-muted-foreground flex items-center gap-2 text-sm">
                <BarChart3 className="w-4 h-4" aria-hidden="true" />
                <span aria-live="polite">Données actualisées au : {lastUpdate}</span>
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full sm:w-auto" role="toolbar" aria-label="Contrôles de la page">
              <PeriodFilter value={periodFilter} onChange={setPeriodFilter} showIcon />
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleRefresh} 
                disabled={isLoading} 
                className="flex items-center gap-2 w-full sm:w-auto"
                aria-label="Actualiser les données"
              >
                <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} aria-hidden="true" />
                <span className="sm:hidden">Actualiser</span>
                <span className="hidden sm:inline">Actualiser</span>
              </Button>
              <EditModeToggle 
                isEditMode={isEditMode} 
                onToggle={toggleEditMode}
                onCancel={cancelEdit}
              />
            </div>
          </div>
        </section>

        {/* Widgets éditables */}
        <section aria-label="Tableaux de bord des ventes">
          <EditableGrid
          widgets={widgets}
          onReorder={reorderWidgets}
          onConfigure={updateWidget}
          onRemove={removeWidget}
          isEditMode={isEditMode}
        >
          {(widget) => (
            <AnalyticsWidget
              widget={widget}
              kpiData={kpiData}
              filteredData={filteredSales}
              periodFilter={periodFilter}
              isLoading={isLoading}
              userRole={userRole}
              onStatusFilterChange={setStatusFilter}
              statusFilter={statusFilter}
              hasAccess={hasAccess}
              productById={productById}
              currentPage={currentPage}
              rowsPerPage={rowsPerPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              onRowsPerPageChange={(rows) => {
                setRowsPerPage(rows);
                setCurrentPage(1);
              }}
            />
          )}
          </EditableGrid>
        </section>

        {/* Bouton d'ajout de widget seulement s'il y a de la place */}
        {isEditMode && hasSpaceForNewWidget && (
          <AddWidgetButton
            onAdd={(type, config) => addWidget(type, config)}
            availableWidgets={[
              { id: 'revenue', type: 'kpi', name: 'Chiffre d\'affaires', config: { kpiType: 'revenue' } },
              { id: 'products-sold', type: 'kpi', name: 'Produits vendus', config: { kpiType: 'products-sold' } },
              { id: 'average-basket', type: 'kpi', name: 'Panier moyen', config: { kpiType: 'average-basket' } },
              { id: 'sales-chart', type: 'chart', name: 'Graphique des ventes', config: { chartType: 'sales-chart' } },
              { id: 'sales-pie', type: 'chart', name: 'Répartition des ventes (camembert)', config: { chartType: 'sales-pie' } },
              { id: 'best-sellers', type: 'table', name: 'Meilleures ventes', config: { tableType: 'best-sellers' } },
              { id: 'sales-list', type: 'table', name: 'Liste des ventes', config: { tableType: 'sales-list' } },
            ]}
          />
        )}
      </div>
    </MainLayout>
  );
};